import React from 'react';
import { Upload, Search, Download, CheckCircle } from 'lucide-react';

type MobilePhase = 'input' | 'processing' | 'output';

interface MobileProgressProps {
  currentPhase: MobilePhase;
  className?: string;
}

export default function MobileProgress({ currentPhase, className = '' }: MobileProgressProps) {
  const phases = [
    {
      key: 'input' as const,
      label: 'Eingabe',
      icon: Upload,
      description: 'Daten hochladen'
    },
    {
      key: 'processing' as const,
      label: 'Bearbeitung',
      icon: Search,
      description: 'Positionen auswählen'
    },
    {
      key: 'output' as const,
      label: 'Ausgabe',
      icon: Download,
      description: 'Angebot erstellen'
    }
  ];

  const getCurrentPhaseIndex = () => {
    return phases.findIndex(phase => phase.key === currentPhase);
  };

  const currentIndex = getCurrentPhaseIndex();

  return (
    <div className={`px-4 py-3 bg-white/90 backdrop-blur border-b border-gray-200 ${className}`}>
      <div className="flex items-center justify-between">
        {phases.map((phase, index) => {
          const Icon = phase.icon;
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;
          const isUpcoming = index > currentIndex;

          return (
            <React.Fragment key={phase.key}>
              {/* Phase Step */}
              <div className="flex flex-col items-center">
                {/* Icon Circle */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                  ${isCompleted ? 'bg-green-500 text-white' : 
                    isActive ? 'bg-blue-500 text-white ring-4 ring-blue-100' : 
                    'bg-gray-200 text-gray-400'}
                `}>
                  {isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <Icon className="h-5 w-5" />
                  )}
                </div>
                
                {/* Label */}
                <div className="mt-2 text-center">
                  <p className={`text-xs font-medium ${
                    isActive ? 'text-blue-600' : 
                    isCompleted ? 'text-green-600' : 
                    'text-gray-400'
                  }`}>
                    {phase.label}
                  </p>
                  <p className={`text-xs ${
                    isActive ? 'text-blue-500' : 
                    isCompleted ? 'text-green-500' : 
                    'text-gray-400'
                  }`}>
                    {phase.description}
                  </p>
                </div>
              </div>

              {/* Connector Line */}
              {index < phases.length - 1 && (
                <div className="flex-1 mx-3">
                  <div className={`
                    h-1 rounded-full transition-all duration-500
                    ${isCompleted ? 'bg-green-500' : 
                      isActive && index < currentIndex ? 'bg-blue-500' : 
                      'bg-gray-200'}
                  `} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
} 